import React, { useState } from 'react';

import { FilterListPopover } from '@modernloop/shared/components';
import { ScorecardFilledIcon } from '@modernloop/shared/icons';
import { Button, ListItem, ListItemButton, Stack, Tooltip, Typography } from '@mui/material';

import { AtsInterviewDefinitionFragment } from 'src/generated/mloop-graphql';

import {
  useGetChildSize,
  useGroupBy,
  useNoScorecardLabel,
  useRenderGroup,
  useRenderOption,
  useScorecardSearchPlaceholderEllipsis,
} from 'src/entities/Ats/AtsScorecard';
import useAtsScorecardOptions from 'src/entities/Ats/useAtsScorecardOptions';

type Props = {
  atsJobId: string;
  selectScorecard?: (interviewDefinition: AtsInterviewDefinitionFragment) => void;
  selectedScorecardName?: string;
  disabled?: boolean;
  disabledReason?: string;
};

const AtsScorecardSelect = ({
  atsJobId,
  selectScorecard,
  selectedScorecardName,
  disabled = false,
  disabledReason,
}: Props) => {
  const { loading, options } = useAtsScorecardOptions(atsJobId);
  const [open, setOpen] = useState(false);

  const handleChange = (event: React.MouseEvent, values: AtsInterviewDefinitionFragment[]) => {
    // The control can become disabled while the list is open (the stage sync setting changed), so
    // the guard lives here and not only on the trigger button.
    if (disabled || !values.length || !selectScorecard) {
      return;
    }
    selectScorecard(values[0]);
    setOpen(false);
  };

  const renderOption = useRenderOption();
  const renderGroup = useRenderGroup();
  const getChildSize = useGetChildSize();
  const groupBy = useGroupBy();
  const noScorecardLabel = useNoScorecardLabel();
  const scorecardSearchPlaceholder = useScorecardSearchPlaceholderEllipsis();
  const fallbackScorecardLabel = options[0]?.name ?? noScorecardLabel;
  const displayScorecardName = selectedScorecardName || fallbackScorecardLabel;

  const selectButton = (
    <Button
      data-testid="ats-scorecard-select"
      sx={{
        maxWidth: '100%',
      }}
      disabled={disabled}
      onClick={() => setOpen(true)}
      startIcon={<ScorecardFilledIcon sx={{ color: 'warning.dark' }} />}
    >
      <Tooltip title={displayScorecardName}>
        <Typography variant="caption" noWrap>
          {displayScorecardName}
        </Typography>
      </Tooltip>
    </Button>
  );

  return (
    <Stack minWidth={0}>
      <FilterListPopover
        open={open && !disabled}
        placeholderText={scorecardSearchPlaceholder}
        isOptionEqualToValue={(option, value) => option.atsId === value.atsId}
        options={options}
        loading={loading}
        onChange={handleChange}
        getOptionLabel={(option) => option.name ?? noScorecardLabel}
        renderOption={(props, option) => {
          return (
            <ListItem {...props}>
              <ListItemButton>{renderOption(option)}</ListItemButton>
            </ListItem>
          );
        }}
        groupBy={groupBy}
        renderGroup={renderGroup}
        getChildSize={getChildSize}
        onPopoverClose={() => setOpen(false)}
      >
        {disabled ? (
          // MUI does not fire tooltip events for a disabled button, so a span carries the tooltip.
          <Tooltip title={disabledReason}>
            <span>{selectButton}</span>
          </Tooltip>
        ) : (
          selectButton
        )}
      </FilterListPopover>
    </Stack>
  );
};

export default AtsScorecardSelect;
